import { Edit, FolderPlus, Heart, MoreHorizontal, Share2, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useSpotifyAuth } from "../../hooks/useSpotifyAuth";
import { useLocalLibraryStore } from "../../stores/localLibraryStore";
import { Track } from "../../types";
import { ConfirmDialog } from "../ConfirmDialog";
import { AddToAlbumDialog } from "./AddToAlbumDialog";
import { EditTrackDialog } from "./EditTrackDialog";

interface TrackMenuProps {
  track: Track;
  onDelete?: () => void;
  onSave?: (updatedTrack: Partial<Track>) => void;
}

export const TrackMenu: React.FC<TrackMenuProps> = ({ track, onDelete, onSave }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddToAlbum, setShowAddToAlbum] = useState(false);
  const [showEditTrack, setShowEditTrack] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const { isContentLiked, toggleLike } = useLocalLibraryStore();
  const { isAuthenticated } = useSpotifyAuth();

  const isLiked = isContentLiked(track.id, "track");

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLikeToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleLike(track.id, "track", track.source);
    setIsOpen(false);
  };

  const handleSave = (updatedTrack: Partial<Track>) => {
    if (onSave) {
      onSave(updatedTrack);
    }
    setShowEditTrack(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 text-zinc-400 hover:text-white transition rounded-full hover:bg-zinc-800"
      >
        <MoreHorizontal className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-zinc-800 rounded-md shadow-lg z-50">
          <div className="py-1">
            <button
              onClick={handleLikeToggle}
              className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-zinc-700"
            >
              <Heart className={`w-4 h-4 ${isLiked ? "text-yellow-500" : ""}`} />
              {isLiked ? "Unlike" : "Like"}
            </button>

            {track.source === "local" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditTrack(true);
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-zinc-700"
              >
                <Edit className="w-4 h-4" />
                Edit Track
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAddToAlbum(true);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-zinc-700"
            >
              <FolderPlus className="w-4 h-4" />
              Add to Album
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                // Share functionality
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-zinc-700"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>

            {onDelete && (
              <>
                <div className="border-t border-zinc-700 my-1" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 hover:bg-zinc-700 text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete!}
        title="Delete Track"
        message={`Are you sure you want to delete "${track.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        type="danger"
      />

      <AddToAlbumDialog isOpen={showAddToAlbum} onClose={() => setShowAddToAlbum(false)} track={track} />

      {track.source === "local" && (
        <EditTrackDialog
          isOpen={showEditTrack}
          onClose={() => setShowEditTrack(false)}
          track={track}
          onSave={handleSave}
        />
      )}
    </div>
  );
};
